import logger from "../utils/logger";
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase, getUserProfile } from '../services/supabase/client';
import { getAttribution } from '../utils/attribution';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use ref to track current user in auth callback (avoid stale closure)
  const userRef = useRef(null);
  const initCompletedRef = useRef(false);

  useEffect(() => {
    // Simple safety timeout - just force loading=false after 10 seconds
    // Don't try to recover session as it causes conflicts with login flow
    const safetyTimeout = setTimeout(() => {
      if (initCompletedRef.current) return; // Already completed

      console.warn('⚠️ Safety timeout reached - forcing loading=false');
      setLoading(false);
      initCompletedRef.current = true;
    }, 10000);

    // Log window focus/blur events to debug
    const handleFocus = () => logger.log('[AuthContext] Window focused');
    const handleBlur = () => logger.log('[AuthContext] Window blurred');

    // Handle visibility change (important for mobile - when user returns to app)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        logger.log('[AuthContext] App became visible - refreshing session');

        // Don't refresh during initial loading to avoid race conditions
        if (loading) {
          logger.log('[AuthContext] Still initializing, skipping refresh');
          return;
        }

        try {
          // Refresh session when app becomes visible
          const { data: { session: refreshedSession }, error } = await supabase.auth.getSession();
          if (error) {
            console.error('[AuthContext] Session refresh error:', error);
            return;
          }

          if (refreshedSession) {
            logger.log('[AuthContext] Session refreshed successfully');
            setSession(refreshedSession);
            setUser(refreshedSession.user);
            userRef.current = refreshedSession.user;
          } else {
            console.warn('[AuthContext] No session found after visibility change');
          }
        } catch (err) {
          console.error('[AuthContext] Error refreshing session:', err);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Get initial session
    const initializeAuth = async () => {
      try {
        logger.log('🔄 Initializing auth...');

        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error('❌ Session fetch error:', error);
          throw error;
        }

        let initialSession = data.session;
        logger.log('📦 Got session:', initialSession ? 'authenticated' : 'No session');

        // Check if session token is expired or about to expire
        if (initialSession) {
          const expiresAt = initialSession.expires_at;
          const now = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = expiresAt - now;

          if (expiresAt && expiresAt < now) {
            console.warn('⚠️ Session token expired, clearing...');
            await supabase.auth.signOut({ scope: 'local' });
            initialSession = null;
          } else if (timeUntilExpiry < 300) {
            // Token expires in less than 5 minutes, refresh it proactively
            logger.log('🔄 Token expires soon, refreshing...');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError) {
              console.error('❌ Error refreshing session:', refreshError);
              await supabase.auth.signOut({ scope: 'local' });
              initialSession = null;
            } else {
              initialSession = refreshData.session;
              logger.log('✅ Session refreshed during initialization');
            }
          }
        }

        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        userRef.current = initialSession?.user ?? null; // Update ref

        // Get user profile if authenticated
        if (initialSession?.user) {
          logger.log('👤 Fetching user profile...');
          try {
            const userProfile = await getUserProfile(initialSession.user.id);
            logger.log('✅ Profile loaded');
            setProfile(userProfile);
          } catch (profileError) {
            console.error('Profile fetch error:', profileError);
            // Continue even if profile fails
          }
        }
        logger.log('✅ Auth initialization complete');
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        // Set loading to false even on error
      } finally {
        logger.log('🏁 Setting loading to false');
        initCompletedRef.current = true; // Mark initialization as complete
        clearTimeout(safetyTimeout); // Clear safety timeout since we finished normally
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      logger.log('[AuthContext] Auth state changed:', event);

      // Clear state immediately on sign out
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        userRef.current = null; // Update ref
        setProfile(null);
        setLoading(false);
        return;
      }

      // Handle TOKEN_REFRESHED - update session but skip profile fetch
      if (event === 'TOKEN_REFRESHED') {
        logger.log('[AuthContext] ✅ Token refreshed - session updated');
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        userRef.current = currentSession?.user ?? null; // Update ref

        // Trigger re-fetch of data in components by updating session timestamp
        // This will cause useEffect hooks to re-run and fetch fresh data
        if (currentSession) {
          logger.log('[AuthContext] Session refreshed at:', new Date().toISOString());
        }

        // Don't fetch profile or change loading state
        return;
      }

      // Always update session and user for SIGNED_IN to ensure LoginForm detects change
      logger.log('[AuthContext] Updating user state:', event);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      userRef.current = currentSession?.user ?? null; // Update ref

      // Update profile when user signs in
      // Skip USER_UPDATED and TOKEN_REFRESHED to avoid unnecessary fetches
      if (currentSession?.user && event !== 'USER_UPDATED') {
        try {
          // Add timeout to prevent hanging on profile fetch (increased to 15s for reliability)
          const profilePromise = getUserProfile(currentSession.user.id);
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Profile fetch timeout')), 15000)
          );

          const userProfile = await Promise.race([profilePromise, timeoutPromise]);
          setProfile(userProfile);
        } catch (error) {
          console.error('Error fetching profile:', error);
          // If we have previous profile state, keep it (preserve trial status)
          // Otherwise set minimal fallback profile
          setProfile((prevProfile) => {
            if (prevProfile) {
              logger.log('[AuthContext] Keeping previous profile after fetch error');
              return prevProfile;
            }
            return {
              id: currentSession.user.id,
              email: currentSession.user.email,
              subscription_plan: 'free',
              properties_count: 0,
            };
          });
        }
      } else if (!currentSession?.user) {
        setProfile(null);
        // Clear OneSignal registration flag when user logs out
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('onesignal_registered_')) {
            sessionStorage.removeItem(key);
          }
        });
      }
      // For USER_UPDATED, keep existing profile (don't fetch again)

      // Always set loading to false, regardless of event type
      setLoading(false);
    });

    return () => {
      initCompletedRef.current = true; // Mark as complete to prevent timeout execution
      clearTimeout(safetyTimeout); // Clear safety timeout on unmount
      subscription.unsubscribe();
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Sign up with email and password
  const signUp = async (email, password, fullName) => {
    try {
      // Attribution goes into user_metadata here — signUp() itself doesn't
      // need an existing session, unlike a profiles insert (which does, and
      // fails RLS this early). getUserProfile()'s fallback later reads it
      // back from the confirmed user's metadata to persist it on the profile.
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            attribution: getAttribution(),
          },
        },
      });

      if (error) throw error;

      // Profile creation happens in getUserProfile()'s fallback once a real
      // session exists (email confirmation is required, so there's no
      // session yet here — an insert attempt at this point always fails RLS).

      return { data, error: null };
    } catch (error) {
      console.error('Error signing up:', error);
      return { data: null, error };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      logger.log('[AuthContext] Starting sign in...');

      // Supabase automatically handles session replacement, no need to clear manually
      // Clearing here can cause race conditions with timeout system
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[AuthContext] Sign in error:', error);
        throw error;
      }

      logger.log('[AuthContext] Sign in successful');
      return { data, error: null };
    } catch (error) {
      console.error('[AuthContext] Error signing in:', error);
      return { data: null, error };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear all state
      setUser(null);
      userRef.current = null; // Update ref
      setProfile(null);
      setSession(null);

      // Force clear localStorage to remove any stale data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error);
      return { error };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error resetting password:', error);
      return { data: null, error };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating password:', error);
      return { data: null, error };
    }
  };

  // Refresh profile (useful after subscription changes)
  const refreshProfile = async () => {
    if (!user) return;

    try {
      const userProfile = await getUserProfile(user.id);
      setProfile(userProfile);
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  // Register user in OneSignal for analytics (runs once per session)
  useEffect(() => {
    const registerInOneSignal = async () => {
      // Don't register if user is not logged in or profile not loaded
      if (!user?.id || !profile || loading) return;

      // Check if already registered in this session (using sessionStorage)
      const sessionKey = `onesignal_registered_${user.id}`;
      if (sessionStorage.getItem(sessionKey)) {
        return; // Already registered in this session
      }

      try {
        logger.log('[OneSignal] Registering user for analytics');

        const { data, error } = await supabase.functions.invoke('register-onesignal-user', {
          body: {
            userId: user.id,
            email: user.email,
            subscriptionPlan: profile.subscription_plan || 'free',
          },
        });

        if (error) {
          console.error('[OneSignal] Registration failed:', error);
        } else {
          logger.log('[OneSignal] User registered successfully');
          // Mark as registered for this session
          sessionStorage.setItem(sessionKey, 'true');
        }
      } catch (error) {
        console.error('[OneSignal] Registration error:', error);
      }
    };

    registerInOneSignal();
  }, [user, profile, loading]);

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
